import {
  Component,
  computed,
  effect,
  input,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';

import {
  PatientTargetModel,
  PatientTargetStatus,
  PatientPlaylistModel,
  PatientProgramModel,
  SkillAreaModel,
  TargetTemplateModel,
  TargetType,
  TargetOrderModel,
} from '../../core/api/models';
import { StaffApiService, SupervisorApiService } from '../../core/api/services';
import { AlertService } from '../../shared/services/alert.service';
import { Utils } from '../../core/utils';
import { SupervisorHelperService } from '../supervisor.helper.service';
import { SupervisorBaseComponent } from '../supervisor-base.component';
import { SelectItem, SelectItemGroup } from 'primeng/api';
import { Router } from '@angular/router';
import { AuthService } from '../../auth/auth.service';
import { filterTargetsForActiveQuickFilter } from '../../shared/utils/utils';
import { AlertHtmlComponent } from '../../shared/alerts/alert-html/alert-html.component';
import { DropdownFilterService } from '../../shared/services/dropdown-filter.service';
import { DateRangeModel } from '../../shared/models/date-range-model';
import { PatientDataFacadeService } from '../../core/services/patient-data-facade.service';
import {
  sortItemsBySupervisorDateOrder,
  supervisorDateOrderOptions,
  SupervisorOrderMode,
  supervisorOrderModeValues,
} from '../supervisor-ordering';

@Component({
  selector: 'app-targets',
  templateUrl: './targets.component.html',
  styleUrl: '../supervisor-main.component.css',
  standalone: false,
})
export class TargetsComponent extends SupervisorBaseComponent {
  patientID = input.required<string>();

  selectedSkillAreas = signal<SkillAreaModel[]>([]);
  selectedPlaylists = signal<PatientPlaylistModel[]>([]);
  selectedPrograms = signal<PatientProgramModel[]>([]);

  // Special option to allow filtering targets not assigned to any program
  private noProgramOption: PatientProgramModel = {
    ID: 0,
    ProgramName: '[No Program]',
  };
  // Special option to allow filtering targets not assigned to any playlist
  private noPlaylistOption: PatientPlaylistModel = {
    ID: 0,
    PlaylistName: '[No Playlist]',
  };
  // Toggle to filter targets not assigned to any program
  filterByOutOfPrograms = signal<boolean>(false);

  selectingForDeletion: boolean = false;
  targetIDsForDeletion: number[] = [];
  doSelectAll: boolean = false;
  errorMessage: string | null = null;

  fullColorTargets = signal<boolean>(false);

  // UI State
  filtersCollapsed = signal<boolean>(true);

  // Order functionality
  orderMode = signal<SupervisorOrderMode>('alphabetical');
  isEditMode = signal<boolean>(false);
  pendingOrderChanges = signal<PatientTargetModel[]>([]);
  lastMovedTargetId = signal<number | null>(null);

  orderOptions = signal<SelectItem[]>([
    { label: 'Alphabetically', value: 'alphabetical' },
    { label: 'by Status', value: 'status' },
    { label: 'Custom', value: 'custom' },
    ...supervisorDateOrderOptions,
  ]);

  possibleTargetTypes = signal<SelectItem[]>([
    {
      label: 'Anecdotal',
      value: TargetType.Anecdotal,
    },
    {
      label: 'Discrete Trial Teaching',
      value: TargetType.DiscreteTrialTeaching,
    },
    {
      label: 'Conditional Discrimination',
      value: TargetType.ConditionalDiscrimination,
    },
    {
      label: 'Duration',
      value: TargetType.Duration,
    },
    {
      label: 'Echoic',
      value: TargetType.Echoic,
    },
    {
      label: 'Fluency Rate',
      value: TargetType.FluencyRate,
    },
    {
      label: 'Rate',
      value: TargetType.Rate,
    },
    {
      label: 'Frequency',
      value: TargetType.Frequency,
    },
    {
      label: 'Grouped Frequency',
      value: TargetType.GroupedFrequency,
    },
    {
      label: 'Jump To',
      value: TargetType.JumpTo,
    },
    {
      label: 'Currency',
      value: TargetType.Currency,
    },
    {
      label: 'Quantity',
      value: TargetType.Quantity,
    },
    {
      label: 'Intensity',
      value: TargetType.Intensity,
    },
    {
      label: 'Task Analysis',
      value: TargetType.TaskAnalysis,
    },
    {
      label: 'Toileting',
      value: TargetType.Toileting,
    },
    {
      label: 'Interval',
      value: TargetType.WholePartialInterval,
    },
  ]);

  possibleStatuses = signal<SelectItem[]>([
    {
      label: 'Ready',
      value: PatientTargetStatus.Ready,
    },
    {
      label: 'Baseline',
      value: PatientTargetStatus.Baseline,
    },
    {
      label: 'Acquisition',
      value: PatientTargetStatus.Aquisition,
    },
    {
      label: 'Mastered',
      value: PatientTargetStatus.Mastered,
    },
    {
      label: 'Maintenance',
      value: PatientTargetStatus.Maintenance,
    },
    {
      label: 'Closed',
      value: PatientTargetStatus.Closed,
    },
    {
      label: 'On Hold',
      value: PatientTargetStatus.On_Hold,
    },
    {
      label: 'Fluent',
      value: PatientTargetStatus.Fluent,
    },
  ]);

  selectedStatuses = signal<PatientTargetStatus[]>([]);
  selectedTargetTypes = signal<TargetType[]>([]);

  searchKeyword = signal<string | null>(null);

  includeDeleted = signal<boolean>(false);
  includeArchived = signal<boolean>(false);
  includeMastered = signal<boolean>(false);
  activeOnly = signal<boolean>(false);

  isExpandedStatus: { [key: number]: boolean } = {};

  openedDateRangeList = signal<SelectItem[]>([]);
  openedDateRange = signal<DateRangeModel | null>(null);
  openedStartDate = signal<string | null>(null);
  openedEndDate = signal<string | null>(null);
  isCustomOpenedDateRange = signal<boolean>(false);

  probedDateRangeList = signal<SelectItem[]>([]);
  probedDateRange = signal<DateRangeModel | null>(null);
  probedStartDate = signal<string | null>(null);
  probedEndDate = signal<string | null>(null);
  isCustomProbedDateRange = signal<boolean>(false);

  masteredDateRangeList = signal<SelectItem[]>([]);
  masteredDateRange = signal<DateRangeModel | null>(null);
  masteredStartDate = signal<string | null>(null);
  masteredEndDate = signal<string | null>(null);
  isCustomMasteredDateRange = signal<boolean>(false);

  closedDateRangeList = signal<SelectItem[]>([]);
  closedDateRange = signal<DateRangeModel | null>(null);
  closedStartDate = signal<string | null>(null);
  closedEndDate = signal<string | null>(null);
  isCustomClosedDateRange = signal<boolean>(false);

  onTargetExpandedChange(targetId: number, isExpanded: boolean) {
    this.isExpandedStatus[targetId] = isExpanded;
  }

  computedFilteredTargets = computed(() => {
    console.log('filteredTargets computed');
    let filteredTargets = this.supervisorHelperService.targets();

    //Avoid loading wrong targets
    if (
      filteredTargets &&
      filteredTargets.length > 0 &&
      filteredTargets[0].PatientID != Number(this.patientID())
    ) {
      console.log('filteredTargets computed: wrong patientID');
      this.loadBaseData(Number(this.patientID()));
      return [];
    }

    // filter out subtargets targets, since we will display them directly in the parent target
    filteredTargets = filteredTargets.filter(
      (target) => !target.IsTargetSubtarget
    );

    // if filter by out of playlists is true, filter out targets that are in playlists
    if (this.filterByOutOfPlaylists()) {
      filteredTargets = filteredTargets.filter(
        (target) => target.PatientPlaylistIDs?.length == 0
      );
    }

    // use keyword from search bar for the first filter
    if (
      this.searchKeyword() != null &&
      this.searchKeyword()!.trim().length > 3
    ) {
      // only filter if keyword is more than 3 characters
      // Match parent target names and nested sub-target names
      const keyword = this.searchKeyword()!.toLowerCase();
      filteredTargets = filteredTargets.filter(
        (target) =>
          target.TargetName?.toLowerCase().includes(keyword) ||
          (target.SubTargets?.some((subTarget) =>
            subTarget.TargetName?.toLowerCase().includes(keyword)
          ) ??
            false)
      );
    }

    // filter out or not the deleted, archived, mastered, inactive, and future targets. depending on the include filters.
    if (!this.includeDeleted()) {
      filteredTargets = filteredTargets.filter((target) => !target.IsDeleted);
    }
    if (!this.includeArchived()) {
      filteredTargets = filteredTargets.filter((target) => !target.IsArchived);
    }
    if (!this.includeMastered()) {
      filteredTargets = filteredTargets.filter(
        (target) =>
          target.TargetStatus != PatientTargetStatus.Mastered &&
          target.TargetStatus != PatientTargetStatus.Fluent
      );
    }
    if (this.activeOnly()) {
      filteredTargets = filterTargetsForActiveQuickFilter(filteredTargets);
    }

    // check for each filter and apply accordingly

    // skill areas
    if (
      this.selectedSkillAreas() != null &&
      this.selectedSkillAreas().length > 0
    ) {
      filteredTargets = filteredTargets.filter((target) =>
        this.selectedSkillAreas().some(
          (skillArea) => skillArea.ID === target.SkillAreaID
        )
      );
    }

    // playlists
    if (
      this.selectedPlaylists() != null &&
      this.selectedPlaylists().length > 0
    ) {
      const selected = this.selectedPlaylists();
      const includesBlank = selected.some((p) => (p.ID ?? 0) === 0);
      const selectedPlaylistIDs = selected
        .map((p) => p.ID ?? 0)
        .filter((id) => id !== 0);

      filteredTargets = filteredTargets.filter((target) => {
        const playlistIDs = target.PatientPlaylistIDs ?? [];
        const matchesSelectedPlaylists =
          selectedPlaylistIDs.length === 0
            ? false
            : playlistIDs.some((id) => selectedPlaylistIDs.includes(id));
        const matchesBlank =
          includesBlank && (playlistIDs == null || playlistIDs.length === 0);
        return matchesSelectedPlaylists || matchesBlank;
      });
    }

    // programs OUT toggle
    if (this.filterByOutOfPrograms()) {
      filteredTargets = filteredTargets.filter(
        (target) => (target.PatientProgramIDs?.length ?? 0) === 0
      );
    }

    // programs
    if (this.selectedPrograms() != null && this.selectedPrograms().length > 0) {
      const selected = this.selectedPrograms();
      const includesBlank = selected.some((p) => (p.ID ?? 0) === 0);
      const selectedProgramIDs = selected
        .map((p) => p.ID ?? 0)
        .filter((id) => id !== 0);

      filteredTargets = filteredTargets.filter((target) => {
        const programIDs = target.PatientProgramIDs ?? [];
        const matchesSelectedPrograms =
          selectedProgramIDs.length === 0
            ? false
            : programIDs.some((id) => selectedProgramIDs.includes(id));
        const matchesBlank =
          includesBlank && (programIDs == null || programIDs.length === 0);
        return matchesSelectedPrograms || matchesBlank;
      });
    }

    // statuses
    if (this.selectedStatuses() != null && this.selectedStatuses().length > 0) {
      filteredTargets = filteredTargets.filter((target) =>
        this.selectedStatuses().some((status) => status === target.TargetStatus)
      );
    }

    // target types
    if (
      this.selectedTargetTypes() != null &&
      this.selectedTargetTypes().length > 0
    ) {
      filteredTargets = filteredTargets.filter((target) =>
        this.selectedTargetTypes().some((type) => type === target.TargetType)
      );
    }

    //opened Date range
    if (this.openedStartDate() != null) {
      filteredTargets = filteredTargets.filter((target) =>
        Utils.dateStringIsBefore(
          this.openedStartDate(),
          target.DateOpened,
          true
        )
      );
    }
    if (this.openedEndDate() != null) {
      filteredTargets = filteredTargets.filter((target) =>
        Utils.dateStringIsBefore(target.DateOpened, this.openedEndDate(), true)
      );
    }

    //probed Date range
    if (this.probedStartDate() != null) {
      filteredTargets = filteredTargets.filter((target) =>
        Utils.dateStringIsBefore(
          this.probedStartDate(),
          target.DateBaselined,
          true
        )
      );
    }
    if (this.probedEndDate() != null) {
      filteredTargets = filteredTargets.filter((target) =>
        Utils.dateStringIsBefore(
          target.DateBaselined,
          this.probedEndDate(),
          true
        )
      );
    }

    //mastered Date range
    if (this.masteredStartDate() != null) {
      filteredTargets = filteredTargets.filter((target) =>
        Utils.dateStringIsBefore(
          this.masteredStartDate(),
          target.DateMastered,
          true
        )
      );
    }
    if (this.masteredEndDate() != null) {
      filteredTargets = filteredTargets.filter((target) =>
        Utils.dateStringIsBefore(
          target.DateMastered,
          this.masteredEndDate(),
          true
        )
      );
    }

    //mastered Date range
    if (this.closedStartDate() != null) {
      filteredTargets = filteredTargets.filter((target) =>
        Utils.dateStringIsBefore(
          this.closedStartDate(),
          target.DateClosed,
          true
        )
      );
    }
    if (this.closedEndDate() != null) {
      filteredTargets = filteredTargets.filter((target) =>
        Utils.dateStringIsBefore(target.DateClosed, this.closedEndDate(), true)
      );
    }

    filteredTargets.sort((a, b) => {
      if (
        (a.TargetStatus === PatientTargetStatus.Closed ||
          a.TargetStatus === PatientTargetStatus.Fluent) &&
        b.TargetStatus !== PatientTargetStatus.Closed &&
        b.TargetStatus !== PatientTargetStatus.Fluent
      ) {
        return 1;
      } else if (
        a.TargetStatus !== PatientTargetStatus.Closed &&
        a.TargetStatus !== PatientTargetStatus.Fluent &&
        (b.TargetStatus === PatientTargetStatus.Closed ||
          b.TargetStatus === PatientTargetStatus.Fluent)
      ) {
        return -1;
      }
      return 0;
    });

    // Apply sorting based on order mode
    const orderMode = this.orderMode();
    if (orderMode === 'alphabetical') {
      // order targets alphabetically
      filteredTargets.sort((a, b) => {
        return a.TargetName?.localeCompare(b.TargetName ?? '') ?? 0;
      });
    } else if (orderMode === 'status') {
      // order targets by status
      filteredTargets.sort((a, b) => {
        const statusOrder = [
          PatientTargetStatus.Ready,
          PatientTargetStatus.Baseline,
          PatientTargetStatus.Aquisition,
          PatientTargetStatus.Mastered,
          PatientTargetStatus.Maintenance,
          PatientTargetStatus.Fluent,
          PatientTargetStatus.Closed,
          PatientTargetStatus.On_Hold,
        ];
        const aIndex = statusOrder.indexOf(
          a.TargetStatus ?? PatientTargetStatus.Ready
        );
        const bIndex = statusOrder.indexOf(
          b.TargetStatus ?? PatientTargetStatus.Ready
        );
        return aIndex - bIndex;
      });
    } else if (orderMode === 'custom') {
      // order targets by OrderNr
      filteredTargets.sort((a, b) => {
        const aOrder = a.OrderNr ?? 0;
        const bOrder = b.OrderNr ?? 0;
        return aOrder - bOrder;
      });
    } else {
      sortItemsBySupervisorDateOrder(
        filteredTargets,
        orderMode,
        (target) => target.DateOpened,
        (target) => target.LastChangedDate
      );
    }

    // return filtered targets
    return filteredTargets;
  });

  filterByOutOfPlaylists = signal<boolean>(false);

  constructor(
    protected override supervisorApiService: SupervisorApiService,
    protected override alertService: AlertService,
    protected override supervisorHelperService: SupervisorHelperService,
    protected override authService: AuthService,
    protected override staffApiService: StaffApiService,
    private dropdownFilterService: DropdownFilterService,
    private router: Router,
    patientDataFacadeService: PatientDataFacadeService
  ) {
    super(
      supervisorApiService,
      alertService,
      supervisorHelperService,
      authService,
      staffApiService,
      patientDataFacadeService
    );
    effect(() => {
      console.log(
        'update signal triggered: ',
        this.supervisorHelperService.updateSignal()
      );
      // Save the current expansion states before the update
      const currentExpandedStatus = { ...this.isExpandedStatus };

      if (this.patientID()) {
        if (!this.isLoading) {
          this.loadBaseData(Number(this.patientID()), () => {
            // Restore the expansion states after data is loaded
            this.isExpandedStatus = currentExpandedStatus;
          });
        }
      } else {
        this.router.navigate(['/select-patient']);
      }
    });
  }

  ngOnInit(): void {
    this.loadOrderModePreference();

    this.openedDateRangeList.set(
      this.dropdownFilterService
        .getLast120DayDateRanges()
        .map((c) => ({ label: c.text, value: c }))
    );
    this.openedDateRange.set(this.openedDateRangeList()[0].value);

    this.probedDateRangeList.set(
      this.dropdownFilterService
        .getLast120DayDateRanges()
        .map((c) => ({ label: c.text, value: c }))
    );
    this.probedDateRange.set(this.probedDateRangeList()[0].value);

    this.masteredDateRangeList.set(
      this.dropdownFilterService
        .getLast120DayDateRanges()
        .map((c) => ({ label: c.text, value: c }))
    );
    this.masteredDateRange.set(this.masteredDateRangeList()[0].value);

    this.closedDateRangeList.set(
      this.dropdownFilterService
        .getLast120DayDateRanges()
        .map((c) => ({ label: c.text, value: c }))
    );
    this.closedDateRange.set(this.closedDateRangeList()[0].value);
  }

  clearSearch() {
    this.selectedSkillAreas.set([]);
    this.selectedPlaylists.set([]);
    this.selectedPrograms.set([]);
    this.selectedStatuses.set([]);
    this.selectedTargetTypes.set([]);
    this.searchKeyword.set(null);
    this.includeDeleted.set(false);
    this.includeArchived.set(false);
    this.includeMastered.set(false);
    this.activeOnly.set(false);
  }

  toggleIncludeDeleted() {
    this.includeDeleted.set(!this.includeDeleted());
    this.exitEditModeIfActive();
  }

  toggleIncludeArchived() {
    this.includeArchived.set(!this.includeArchived());
    this.exitEditModeIfActive();
  }

  toggleIncludeMastered() {
    this.includeMastered.set(!this.includeMastered());
    this.exitEditModeIfActive();
  }

  toggleActiveOnly() {
    this.activeOnly.set(!this.activeOnly());
    this.exitEditModeIfActive();
  }

  addTargetButtonClicked() {
    console.log('addTargetButtonClicked');
    this.supervisorHelperService.additTargetButtonClicked(null);
  }

  toggleIsSelecting(): void {
    this.selectingForDeletion = !this.selectingForDeletion;
  }

  cancelIsSelecting(): void {
    this.selectingForDeletion = false;
    this.targetIDsForDeletion = [];
  }

  selectAllTargets(): void {
    this.doSelectAll = !this.doSelectAll;
    if (this.doSelectAll == false) this.targetIDsForDeletion = [];
  }

  private static readonly applyToAllSubTargetsOption =
    'Apply to all sub-targets';
  private static readonly detachSubTargetsOption =
    'Detach as individual targets';

  deleteSelectedTargets(): void {
    if (this.selectingForDeletion && this.targetIDsForDeletion.length > 0) {
      // First confirmation: Ask about deletion
      this.alertService
        .confirm(
          'Confirm Deletion',
          `Are you sure you want to mark ${this.targetIDsForDeletion.length} target(s) as deleted?`
        )
        .subscribe((confirmed) => {
          if (confirmed) {
            const selectedHaveSubTargets = this.targetIDsForDeletion.some(
              (id) => {
                const target = this.supervisorHelperService
                  .targets()
                  .find((t) => t.ID === id);
                return (target?.SubTargets?.length ?? 0) > 0;
              }
            );

            if (!selectedHaveSubTargets) {
              this.executeBatchDelete(false);
              return;
            }

            // Add a small delay to ensure the first dialog is fully closed
            setTimeout(() => {
              this.alertService
                .chooseOption(
                  'Sub-targets',
                  'One or more selected targets have sub-targets. Choose whether to delete those sub-targets as well, or detach them so they become individual targets of their own type.',
                  [
                    TargetsComponent.applyToAllSubTargetsOption,
                    TargetsComponent.detachSubTargetsOption,
                  ]
                )
                .subscribe((option) => {
                  if (option === TargetsComponent.applyToAllSubTargetsOption) {
                    this.executeBatchDelete(true);
                  } else if (
                    option === TargetsComponent.detachSubTargetsOption
                  ) {
                    this.executeBatchDelete(false);
                  }
                });
            }, 100);
          }
        });
    }
  }

  private executeBatchDelete(propagate: boolean): void {
    this.isLoading = true;
    this.supervisorApiService
      .MarkMultiplePatientTargetsAsDeleted({
        body: {
          PatientID: Number(this.patientID()),
          TargetIDs: this.targetIDsForDeletion,
          Propagate: propagate,
        },
      })
      .subscribe({
        next: () => {
          this.alertService.success('Targets marked as deleted');
          this.supervisorHelperService.triggerUpdateSignal();
          this.cancelIsSelecting();
        },
        complete: () => {
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          var result = JSON.parse(error);
          this.errorMessage = result.Message;
          setTimeout(() => (this.errorMessage = null), 10000);
          this.isLoading = false;
          this.supervisorHelperService.triggerUpdateSignal();
        },
      });
  }

  closeError() {
    this.errorMessage = '';
  }

  selectTarget(targetID: number) {
    if (this.targetIDsForDeletion.includes(targetID)) {
      this.targetIDsForDeletion = this.targetIDsForDeletion.filter(
        (id) => id !== targetID
      );
    } else {
      this.targetIDsForDeletion.push(targetID);
    }
    console.log(this.targetIDsForDeletion);
  }

  toggleFullColorTargets() {
    this.fullColorTargets.set(!this.fullColorTargets());
  }

  toggleFilterByOutOfPlaylists() {
    this.filterByOutOfPlaylists.set(!this.filterByOutOfPlaylists());
    this.exitEditModeIfActive();
  }

  toggleFilterByOutOfPrograms() {
    this.filterByOutOfPrograms.set(!this.filterByOutOfPrograms());
    this.exitEditModeIfActive();
  }

  exitEditModeIfActive() {
    if (this.isEditMode()) {
      this.isEditMode.set(false);
      this.pendingOrderChanges.set([]);
    }
  }

  changedOpenedDateRange() {
    if (!this.openedDateRange()) {
      this.isCustomOpenedDateRange.set(false);
      this.openedStartDate.set(null);
      this.openedEndDate.set(null);
      this.exitEditModeIfActive();
      return;
    }

    if (this.openedDateRange()!.isCustom) {
      this.isCustomOpenedDateRange.set(true);
      return;
    }

    this.isCustomOpenedDateRange.set(false);
    this.openedStartDate.set(this.openedDateRange()!.startDate);
    this.openedEndDate.set(this.openedDateRange()!.endDate);
    this.exitEditModeIfActive();
  }

  changedProbedDateRange() {
    if (!this.probedDateRange()) {
      this.isCustomProbedDateRange.set(false);
      this.probedStartDate.set(null);
      this.probedEndDate.set(null);
      this.exitEditModeIfActive();
      return;
    }

    if (this.probedDateRange()!.isCustom) {
      this.isCustomProbedDateRange.set(true);
      return;
    }

    this.isCustomProbedDateRange.set(false);
    this.probedStartDate.set(this.probedDateRange()!.startDate);
    this.probedEndDate.set(this.probedDateRange()!.endDate);
    this.exitEditModeIfActive();
  }

  changedMasteredDateRange() {
    if (!this.masteredDateRange()) {
      this.isCustomMasteredDateRange.set(false);
      this.masteredStartDate.set(null);
      this.masteredEndDate.set(null);
      this.exitEditModeIfActive();
      return;
    }

    if (this.masteredDateRange()!.isCustom) {
      this.isCustomMasteredDateRange.set(true);
      return;
    }

    this.isCustomMasteredDateRange.set(false);
    this.masteredStartDate.set(this.masteredDateRange()!.startDate);
    this.masteredEndDate.set(this.masteredDateRange()!.endDate);
    this.exitEditModeIfActive();
  }

  changedClosedDateRange() {
    if (!this.closedDateRange()) {
      this.isCustomClosedDateRange.set(false);
      this.closedStartDate.set(null);
      this.closedEndDate.set(null);
      this.exitEditModeIfActive();
      return;
    }

    if (this.closedDateRange()!.isCustom) {
      this.isCustomClosedDateRange.set(true);
      return;
    }

    this.isCustomClosedDateRange.set(false);
    this.closedStartDate.set(this.closedDateRange()!.startDate);
    this.closedEndDate.set(this.closedDateRange()!.endDate);
    this.exitEditModeIfActive();
  }

  toggleFiltersCollapsed(): void {
    this.filtersCollapsed.set(!this.filtersCollapsed());
  }

  getPlaylistOptions(): PatientPlaylistModel[] {
    // Prepend a blank option for filtering targets with no playlist
    return [this.noPlaylistOption, ...this.supervisorHelperService.playlists()];
  }

  getProgramOptions(): PatientProgramModel[] {
    // Prepend a blank option for filtering targets with no program
    return [this.noProgramOption, ...this.supervisorHelperService.programs()];
  }

  // Order functionality methods
  onOrderModeChange() {
    // Save the order mode preference
    this.saveOrderModePreference();
  }

  toggleEditMode() {
    if (this.isEditMode()) {
      // Exiting edit mode - save all pending changes
      this.saveAllPendingChanges();
    } else {
      // Starting edit mode - check if filters are active
      if (this.hasActiveFilters()) {
        this.alertService
          .confirm(
            'Clear Filters',
            'To start configuring a custom order for this list we must first clear the filters, click "yes" to reset the filters and start editing, "no" to cancel'
          )
          .subscribe((result) => {
            if (result) {
              this.clearAllFilters();
              this.isEditMode.set(true);
            }
          });
        return;
      }
    }
    this.isEditMode.set(!this.isEditMode());
  }

  private hasActiveFilters(): boolean {
    return (
      (this.selectedSkillAreas() && this.selectedSkillAreas().length > 0) ||
      (this.selectedPlaylists() && this.selectedPlaylists().length > 0) ||
      (this.selectedPrograms() && this.selectedPrograms().length > 0) ||
      (this.selectedStatuses() && this.selectedStatuses().length > 0) ||
      (this.selectedTargetTypes() && this.selectedTargetTypes().length > 0) ||
      (this.searchKeyword() && this.searchKeyword() !== '') ||
      this.includeDeleted() ||
      this.includeArchived() ||
      this.includeMastered() ||
      this.activeOnly()
    );
  }

  private clearAllFilters(): void {
    this.selectedSkillAreas.set([]);
    this.selectedPlaylists.set([]);
    this.selectedPrograms.set([]);
    this.selectedStatuses.set([]);
    this.selectedTargetTypes.set([]);
    this.searchKeyword.set(null);
    this.includeDeleted.set(false);
    this.includeArchived.set(false);
    this.includeMastered.set(false);
    this.activeOnly.set(false);
  }

  moveTargetUp(targetId: number) {
    if (this.orderMode() === 'custom') {
      const targets = this.computedFilteredTargets();
      const currentIndex = targets.findIndex((t) => t.ID === targetId);

      if (currentIndex > 0) {
        // Track the last moved target
        this.lastMovedTargetId.set(targetId);

        // Swap with previous target
        const temp = targets[currentIndex];
        targets[currentIndex] = targets[currentIndex - 1];
        targets[currentIndex - 1] = temp;

        // Update OrderNr for all targets
        targets.forEach((t, index) => {
          t.OrderNr = index + 1;
        });

        // Add all targets to pending changes
        this.addToPendingChanges(targets);
      }
    }
  }

  moveTargetDown(targetId: number) {
    if (this.orderMode() === 'custom') {
      const targets = this.computedFilteredTargets();
      const currentIndex = targets.findIndex((t) => t.ID === targetId);

      if (currentIndex < targets.length - 1) {
        // Track the last moved target
        this.lastMovedTargetId.set(targetId);

        // Swap with next target
        const temp = targets[currentIndex];
        targets[currentIndex] = targets[currentIndex + 1];
        targets[currentIndex + 1] = temp;

        // Update OrderNr for all targets
        targets.forEach((t, index) => {
          t.OrderNr = index + 1;
        });

        // Add all targets to pending changes
        this.addToPendingChanges(targets);
      }
    }
  }

  moveTargetToTop(targetId: number) {
    if (this.orderMode() === 'custom') {
      const targets = this.computedFilteredTargets();
      const currentIndex = targets.findIndex((t) => t.ID === targetId);

      if (currentIndex > 0) {
        // Track the last moved target
        this.lastMovedTargetId.set(targetId);

        // Move to top
        const target = targets.splice(currentIndex, 1)[0];
        targets.unshift(target);

        // Update OrderNr for all targets
        targets.forEach((t, index) => {
          t.OrderNr = index + 1;
        });

        // Add all affected targets to pending changes
        this.addToPendingChanges(targets.slice(0, currentIndex + 1));
      }
    }
  }

  moveTargetToBottom(targetId: number) {
    if (this.orderMode() === 'custom') {
      const targets = this.computedFilteredTargets();
      const currentIndex = targets.findIndex((t) => t.ID === targetId);

      if (currentIndex < targets.length - 1) {
        // Track the last moved target
        this.lastMovedTargetId.set(targetId);

        // Move to bottom
        const target = targets.splice(currentIndex, 1)[0];
        targets.push(target);

        // Update OrderNr for all targets
        targets.forEach((t, index) => {
          t.OrderNr = index + 1;
        });

        // Add all affected targets to pending changes
        this.addToPendingChanges(targets.slice(currentIndex));
      }
    }
  }

  private addToPendingChanges(targets: PatientTargetModel[]) {
    const currentPending = this.pendingOrderChanges();
    const newPending = [...currentPending];

    targets.forEach((target) => {
      const existingIndex = newPending.findIndex((t) => t.ID === target.ID);
      if (existingIndex >= 0) {
        newPending[existingIndex] = target;
      } else {
        newPending.push(target);
      }
    });

    this.pendingOrderChanges.set(newPending);
  }

  private saveAllPendingChanges(): void {
    const pendingChanges = this.pendingOrderChanges();

    if (pendingChanges.length === 0) {
      return;
    }

    // Get all targets (not just filtered ones) to ensure proper order values for all items
    const allTargets = this.supervisorHelperService.targets();

    // Create order dictionary with all targets
    const targetOrders: { [key: string]: number | null } = {};

    // First, set order for all targets based on their current OrderNr
    allTargets.forEach((target) => {
      if (target.OrderNr !== null && target.OrderNr !== undefined) {
        targetOrders[target.ID!.toString()] = target.OrderNr;
      }
    });

    // Then update with the pending changes
    pendingChanges.forEach((target) => {
      if (target.OrderNr !== null && target.OrderNr !== undefined) {
        targetOrders[target.ID!.toString()] = target.OrderNr;
      }
    });

    // Create the order model
    const orderModel: TargetOrderModel = {
      PatientID: Number(this.patientID()),
      TargetOrders: targetOrders,
    };

    // Save all order changes in a single call
    this.supervisorApiService
      .SaveTargetOrder({
        body: orderModel,
      })
      .subscribe({
        next: () => {
          console.log('All target order changes saved successfully');
          // Clear pending changes
          this.pendingOrderChanges.set([]);
          // Trigger a refresh to get the updated data
          this.loadBaseData(Number(this.patientID()));
        },
        error: (error) => {
          console.error('Error saving target order changes:', error);
          this.alertService.error('Failed to save target order changes');
        },
      });
  }

  private saveOrderModePreference(): void {
    const patientId = this.patientID();
    const orderMode = this.orderMode();
    if (patientId) {
      sessionStorage.setItem(`targets_order_mode_${patientId}`, orderMode);
    }
  }

  private loadOrderModePreference(): void {
    const patientId = this.patientID();
    if (patientId) {
      const savedOrderMode = sessionStorage.getItem(
        `targets_order_mode_${patientId}`
      ) as SupervisorOrderMode | null;
      if (savedOrderMode && supervisorOrderModeValues.includes(savedOrderMode)) {
        this.orderMode.set(savedOrderMode);
      }
    }
  }

  isFirstTarget(targetId: number): boolean {
    return (
      this.computedFilteredTargets().findIndex(
        (t: any) => t.ID === targetId
      ) === 0
    );
  }

  isLastTarget(targetId: number): boolean {
    return (
      this.computedFilteredTargets().findIndex(
        (t: any) => t.ID === targetId
      ) ===
      this.computedFilteredTargets().length - 1
    );
  }

  hasPendingChanges(targetId: number): boolean {
    return this.lastMovedTargetId() === targetId;
  }

  getTotalTargetsCount(): number {
    return this.computedFilteredTargets().length;
  }
}
